/**
 * Resolver HTTP cache policy constants (all scan and linkset responses use `no-store`).
 */
export const RESOLVER_CACHE_TTLS = {
    /** Scan and linkset responses must never be stored by intermediate caches. */
    SCAN_CACHE_CONTROL: 'no-store',
};
export const ALLOWED_FAULT_INJECTION_PROJECT_ID = 'mypack-dev';
export const DEFAULT_GTIN_CHECK_ENDPOINT_URL = 'https://gtincheck.gs1uk.org';
export const DEFAULT_GTIN_CHECK_SECRET_NAME = 'gs1uk-gtincheck-key';
export const DEFAULT_GTIN_CHECK_MAX_BATCH_SIZE = 1_000;
export const DEFAULT_GTIN_CHECK_MAX_PAYLOAD_BYTES = 200_000;
export const DEFAULT_GTIN_CHECK_MAX_CONCURRENCY = 2;
export const DEFAULT_WEBRISK_ENDPOINT_URL = 'https://webrisk.googleapis.com/v1/uris:search';
export const DEFAULT_WEBRISK_THREAT_TYPES = [
    'MALWARE',
    'SOCIAL_ENGINEERING',
    'UNWANTED_SOFTWARE',
];
export const DEFAULT_SMTP_SENDER_ADDRESS = 'no-reply@myplayr.cloud';
export const DEFAULT_SMTP_REPLY_TO_ADDRESS = 'support@myplayr.cloud';
export const DEFAULT_SMTP_RELAY_HOST = 'smtp-relay.gmail.com';
export const DEFAULT_SMTP_RELAY_PORT = 465;
export const DEFAULT_SMTP_RELAY_TLS = true;
export const DEFAULT_SMTP_SECRET_NAME = 'smtp-relay-credentials';
export function isGtinCheckEnabled(rawEnv = process.env) {
    const raw = (rawEnv['GTIN_CHECK_ENABLED'] ?? rawEnv['gtinCheck.enabled'])?.trim().toLowerCase();
    return raw === 'true' || raw === '1';
}
export function loadGtinCheckFeatureConfig(rawEnv = process.env) {
    const endpointUrl = (rawEnv['GTIN_CHECK_ENDPOINT_URL'] ?? DEFAULT_GTIN_CHECK_ENDPOINT_URL).trim();
    const secretName = (rawEnv['GTIN_CHECK_SECRET_NAME'] ?? DEFAULT_GTIN_CHECK_SECRET_NAME).trim();
    const maxBatchRaw = Number(rawEnv['GTIN_CHECK_MAX_BATCH_SIZE'] ?? String(DEFAULT_GTIN_CHECK_MAX_BATCH_SIZE));
    const maxBatchSize = Number.isInteger(maxBatchRaw) &&
        maxBatchRaw > 0 &&
        maxBatchRaw <= DEFAULT_GTIN_CHECK_MAX_BATCH_SIZE
        ? maxBatchRaw
        : DEFAULT_GTIN_CHECK_MAX_BATCH_SIZE;
    const maxPayloadRaw = Number(rawEnv['GTIN_CHECK_MAX_PAYLOAD_BYTES'] ?? String(DEFAULT_GTIN_CHECK_MAX_PAYLOAD_BYTES));
    const maxPayloadBytes = Number.isInteger(maxPayloadRaw) &&
        maxPayloadRaw > 0 &&
        maxPayloadRaw <= DEFAULT_GTIN_CHECK_MAX_PAYLOAD_BYTES
        ? maxPayloadRaw
        : DEFAULT_GTIN_CHECK_MAX_PAYLOAD_BYTES;
    const maxConcurrencyRaw = Number(rawEnv['GTIN_CHECK_MAX_CONCURRENCY'] ?? String(DEFAULT_GTIN_CHECK_MAX_CONCURRENCY));
    const maxConcurrency = Number.isInteger(maxConcurrencyRaw) && maxConcurrencyRaw > 0
        ? maxConcurrencyRaw
        : DEFAULT_GTIN_CHECK_MAX_CONCURRENCY;
    return {
        enabled: isGtinCheckEnabled(rawEnv),
        endpointUrl,
        secretName,
        maxBatchSize,
        maxPayloadBytes,
        maxConcurrency,
    };
}
export function isCdnEnabled(rawEnv = process.env) {
    const raw = rawEnv['CDN_ENABLED']?.trim().toLowerCase();
    return raw === 'true' || raw === '1';
}
function parseFaultInjectionRate(rawEnv, keys) {
    for (const key of keys) {
        const raw = rawEnv[key]?.trim();
        if (raw !== undefined && raw.length > 0) {
            const parsed = Number(raw);
            if (!Number.isFinite(parsed) || parsed < 0 || parsed > 1) {
                throw new Error(`Invalid fault injection rate ${key}="${raw}": must be a number in [0, 1]`);
            }
            return parsed;
        }
    }
    return 0;
}
export function loadFaultInjectionConfig(rawEnv = process.env, explicitProjectId) {
    const rawEnabled = rawEnv['FAULT_INJECTION']?.trim();
    if (rawEnabled === undefined || rawEnabled === '' || rawEnabled.toLowerCase() === 'false') {
        return {
            enabled: false,
            analyticsTxFailRate: 0,
            pubsubPublishFailRate: 0,
        };
    }
    if (rawEnabled.toLowerCase() !== 'true') {
        throw new Error(`Invalid FAULT_INJECTION="${rawEnabled}": must be "true" or "false" (default unset/false).`);
    }
    const projectId = (explicitProjectId?.trim() ||
        rawEnv['GCP_PROJECT_ID']?.trim() ||
        rawEnv['GOOGLE_CLOUD_PROJECT']?.trim() ||
        rawEnv['GCLOUD_PROJECT']?.trim() ||
        'myplayer-gs1-local').trim();
    const envVal = (rawEnv['MYPLAYER_ENV'] ?? 'local').trim();
    if (projectId !== ALLOWED_FAULT_INJECTION_PROJECT_ID ||
        envVal === 'staging' ||
        envVal === 'production') {
        throw new Error(`FAULT_INJECTION=true is rejected at startup: project "${projectId}" (env "${envVal}") is not "${ALLOWED_FAULT_INJECTION_PROJECT_ID}". Dev-only fault injection is only allowed in "${ALLOWED_FAULT_INJECTION_PROJECT_ID}".`);
    }
    return {
        enabled: true,
        analyticsTxFailRate: parseFaultInjectionRate(rawEnv, [
            'FAULT_INJECTION_ANALYTICS_TX_FAIL_RATE',
            'SCAN_ANALYTICS_TX_FAIL_RATE',
            'FAULT_INJECTION_TX_FAIL_RATE',
            'ANALYTICS_TX_FAIL_RATE',
        ]),
        pubsubPublishFailRate: parseFaultInjectionRate(rawEnv, [
            'FAULT_INJECTION_PUBSUB_PUBLISH_FAIL_RATE',
            'PUBSUB_PUBLISH_FAIL_RATE',
            'FAULT_INJECTION_PUBLISH_FAIL_RATE',
            'RESOLVER_PUBLISH_FAIL_RATE',
        ]),
    };
}
export function shouldInjectFault(rate, randomFn = Math.random) {
    if (!Number.isFinite(rate) || rate <= 0) {
        return false;
    }
    if (rate >= 1) {
        return true;
    }
    return randomFn() < rate;
}
export const PUBLIC_TEST_IP_HASH_FALLBACK_KEY = 'mp-local-dev-ip-hash-hmac-key';
export const DEFAULT_PLATFORM_RESOLVER_DOMAIN = 'myplayr.cloud';
export const DEFAULT_LOCAL_RESOLVER_PORT = 8090;
export const DEFAULT_LOCAL_RESOLVER_ORIGIN = `http://localhost:${String(DEFAULT_LOCAL_RESOLVER_PORT)}`;
export const DEFAULT_PLATFORM_FALLBACK_URL = `https://${DEFAULT_PLATFORM_RESOLVER_DOMAIN}/fallback`;
export function resolveIpHashSecretKey(rawEnv = process.env, overrideSecret) {
    const envVal = rawEnv['MYPLAYER_ENV'] ?? 'local';
    if (envVal !== 'local' && envVal !== 'staging' && envVal !== 'production') {
        throw new Error(`Invalid MYPLAYER_ENV: ${envVal}`);
    }
    const isTestEnv = rawEnv['NODE_ENV'] === 'test' ||
        rawEnv['VITEST'] === 'true' ||
        (rawEnv['VITEST'] === undefined &&
            rawEnv['NODE_ENV'] === undefined &&
            (process.env['NODE_ENV'] === 'test' || process.env['VITEST'] === 'true'));
    const configuredIpHashKey = (overrideSecret ?? rawEnv['IP_HASH_SECRET_KEY'])?.trim();
    const isRestrictedRuntime = envVal !== 'local' || rawEnv['NODE_ENV'] === 'production' || !isTestEnv;
    if (configuredIpHashKey === PUBLIC_TEST_IP_HASH_FALLBACK_KEY) {
        throw new Error('IP_HASH_SECRET_KEY cannot be set to the public fallback key mp-local-dev-ip-hash-hmac-key; mount a real secret from Secret Manager.');
    }
    if (isRestrictedRuntime && (!configuredIpHashKey || configuredIpHashKey.length === 0)) {
        throw new Error('IP_HASH_SECRET_KEY is required outside tests and in staging/production (fail closed).');
    }
    return configuredIpHashKey && configuredIpHashKey.length > 0
        ? configuredIpHashKey
        : PUBLIC_TEST_IP_HASH_FALLBACK_KEY;
}
export function loadPlatformConfig(rawEnv = process.env, options) {
    const envVal = rawEnv['MYPLAYER_ENV'] ?? 'local';
    if (envVal !== 'local' && envVal !== 'staging' && envVal !== 'production') {
        throw new Error(`Invalid MYPLAYER_ENV: ${envVal}`);
    }
    const resolveSecret = () => options?.resolveIpHashSecret ? options.resolveIpHashSecret() : resolveIpHashSecretKey(rawEnv);
    const eagerIpHashSecretKey = options?.lazySecrets === true ? undefined : resolveSecret();
    const redisPort = Number(rawEnv['REDIS_PORT'] ?? '6379');
    if (!Number.isInteger(redisPort) || redisPort <= 0 || redisPort > 65535) {
        throw new Error(`Invalid REDIS_PORT: ${String(rawEnv['REDIS_PORT'])}`);
    }
    const resolverPort = Number(rawEnv['PORT'] ?? '8080');
    if (!Number.isInteger(resolverPort) || resolverPort <= 0 || resolverPort > 65535) {
        throw new Error(`Invalid PORT: ${String(rawEnv['PORT'])}`);
    }
    const regionalReadMaxLagSeconds = Number(rawEnv['REGIONAL_READ_MAX_LAG_SECONDS'] ?? '30');
    if (!Number.isFinite(regionalReadMaxLagSeconds) || regionalReadMaxLagSeconds < 0) {
        throw new Error(`Invalid REGIONAL_READ_MAX_LAG_SECONDS: ${String(rawEnv['REGIONAL_READ_MAX_LAG_SECONDS'])}`);
    }
    const resolverDefaultDomain = (rawEnv['RESOLVER_DEFAULT_DOMAIN'] ?? DEFAULT_PLATFORM_RESOLVER_DOMAIN)
        .trim()
        .toLowerCase();
    const extraHosts = (rawEnv['RESOLVER_ALLOWED_HOSTS'] ?? '')
        .split(',')
        .map((h) => h.trim().toLowerCase())
        .filter((h) => h.length > 0);
    const localDevHosts = envVal === 'local' ? ['localhost', '127.0.0.1'] : [];
    const allowedHosts = [...new Set([resolverDefaultDomain, ...extraHosts, ...localDevHosts])];
    const gcpProjectId = rawEnv['GCP_PROJECT_ID']?.trim() ||
        rawEnv['GOOGLE_CLOUD_PROJECT']?.trim() ||
        rawEnv['GCLOUD_PROJECT']?.trim() ||
        'myplayer-gs1-local';
    const faultInjection = loadFaultInjectionConfig(rawEnv, gcpProjectId);
    return {
        env: envVal,
        gcpProjectId,
        primaryRegion: rawEnv['PRIMARY_REGION'] ?? 'europe-west2',
        resolverDefaultDomain,
        allowedHosts,
        redisHost: rawEnv['REDIS_HOST'] ?? '127.0.0.1',
        redisPort,
        resolverPort,
        holdingPageUrl: rawEnv['HOLDING_PAGE_URL'] ?? `https://${resolverDefaultDomain}/suspended`,
        platformDefaultFallbackUrl: rawEnv['PLATFORM_DEFAULT_FALLBACK_URL'] ?? `https://${resolverDefaultDomain}`,
        abuseContactEmail: rawEnv['ABUSE_CONTACT_EMAIL'] ?? 'mailto:abuse@myplayer.uk',
        extensionProfileUrl: rawEnv['EXTENSION_PROFILE_URL'] ??
            `https://${resolverDefaultDomain}/.well-known/gs1-extensions.json`,
        gs1VocabNamespace: rawEnv['GS1_VOCAB_NAMESPACE'] ?? 'https://ref.gs1.org/voc/',
        linksetContextUrl: rawEnv['LINKSET_CONTEXT_URL'] ?? 'https://ref.gs1.org/standards/resolver/linkset-context',
        regionalReadMaxLagSeconds,
        cdnEnabled: isCdnEnabled(rawEnv),
        gtinCheck: loadGtinCheckFeatureConfig(rawEnv),
        faultInjection,
        get ipHashSecretKey() {
            return eagerIpHashSecretKey ?? resolveSecret();
        },
    };
}
//# sourceMappingURL=index.js.map